/**
 * CTRL_03 Dashboard - Model Provenance & Versioning
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress, Alert } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CodeOutlined,
  GitlabOutlined,
  FileTextOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function CTRL_03_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_03';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate version coverage
  const versionCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, tracked: 0, percentage: 0 };

    const tracked = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.model_version || e.model;
    }).length;

    return {
      total: events.length,
      tracked,
      percentage: Math.round((tracked / events.length) * 100)
    };
  }, [events]);

  // Calculate lineage tracking
  const lineageTracking = useMemo(() => {
    const withLineage = events.filter(e =>
      e.event_metadata?.data_lineage
    ).length;

    return {
      total: events.length,
      withLineage,
      percentage: events.length > 0 ? Math.round((withLineage / events.length) * 100) : 0
    };
  }, [events]);

  // Calculate source repo coverage
  const sourceRepoCoverage = useMemo(() => {
    const withRepo = events.filter(e =>
      e.event_metadata?.source_repo
    ).length;

    return {
      total: events.length,
      withRepo,
      percentage: events.length > 0 ? Math.round((withRepo / events.length) * 100) : 0
    };
  }, [events]);

  // Calculate model cards
  const modelCards = useMemo(() => {
    const withCards = events.filter(e =>
      e.event_metadata?.model_card_url || e.event_metadata?.documentation_url
    ).length;

    return {
      total: events.length,
      withCards,
      percentage: events.length > 0 ? Math.round((withCards / events.length) * 100) : 0
    };
  }, [events]);

  // Find violations
  const violations = useMemo(() => {
    return events.filter(e => {
      const metadata = e.event_metadata || {};
      return !metadata.model_version && !e.model;
    });
  }, [events]);

  // Recent models table
  const recentModels = useMemo(() => {
    return events
      .filter(e => e.event_metadata?.model_version || e.model)
      .slice(0, 20);
  }, [events]);

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      ellipsis: true,
      render: (model) => model || '-'
    },
    {
      title: 'Version',
      key: 'version',
      render: (_, record) => {
        const version = record.event_metadata?.model_version;
        return version ?
          <Tag color="blue" icon={<CodeOutlined />}>{version}</Tag> :
          <Tag color="orange">Missing</Tag>;
      }
    },
    {
      title: 'Source Repo',
      dataIndex: ['event_metadata', 'source_repo'],
      key: 'repo',
      ellipsis: true,
      render: (repo) => repo ?
        <Tag color="green" icon={<GitlabOutlined />}>{repo}</Tag> :
        <Tag color="red">Missing</Tag>
    },
    {
      title: 'Training Date',
      dataIndex: ['event_metadata', 'training_date'],
      key: 'training_date',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: 'Lineage',
      key: 'lineage',
      align: 'center',
      render: (_, record) => {
        const hasLineage = record.event_metadata?.data_lineage;
        return hasLineage ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Tracked</Tag> :
          <Tag color="orange">None</Tag>;
      }
    },
    {
      title: 'Model Card',
      key: 'card',
      align: 'center',
      render: (_, record) => {
        const hasCard = record.event_metadata?.model_card_url || record.event_metadata?.documentation_url;
        return hasCard ?
          <Tag color="green" icon={<FileTextOutlined />}>Yes</Tag> :
          <Tag color="orange">No</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_03: Model Provenance & Versioning Dashboard</h2>

      {violations.length > 0 && (
        <Alert
          message={`${violations.length} models missing version information`}
          description="Some models lack proper provenance tracking. Enable version control and data lineage."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
          icon={<WarningOutlined />}
        />
      )}

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Version Coverage"
              value={versionCoverage.percentage}
              suffix="%"
              prefix={versionCoverage.percentage >= 80 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: versionCoverage.percentage >= 80 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {versionCoverage.tracked} of {versionCoverage.total} tracked
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Lineage Tracking"
              value={lineageTracking.percentage}
              suffix="%"
              prefix={<CalendarOutlined />}
              valueStyle={{ color: lineageTracking.percentage >= 70 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {lineageTracking.withLineage} with data lineage
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Source Repo Coverage"
              value={sourceRepoCoverage.percentage}
              suffix="%"
              prefix={<GitlabOutlined />}
              valueStyle={{ color: sourceRepoCoverage.percentage >= 80 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {sourceRepoCoverage.withRepo} with repo info
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Model Cards"
              value={modelCards.percentage}
              suffix="%"
              prefix={<FileTextOutlined />}
              valueStyle={{ color: modelCards.percentage >= 60 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {modelCards.withCards} documented
            </div>
          </Card>
        </Col>
      </Row>

      {/* Coverage Progress Bars */}
      <Card title="Provenance Tracking Overview" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Model Version</span>
                <span style={{ fontWeight: 'bold' }}>{versionCoverage.percentage}%</span>
              </div>
              <Progress
                percent={versionCoverage.percentage}
                strokeColor={versionCoverage.percentage >= 80 ? '#52c41a' : '#faad14'}
                status={versionCoverage.percentage >= 80 ? 'success' : 'active'}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Data Lineage</span>
                <span style={{ fontWeight: 'bold' }}>{lineageTracking.percentage}%</span>
              </div>
              <Progress
                percent={lineageTracking.percentage}
                strokeColor={lineageTracking.percentage >= 70 ? '#52c41a' : '#faad14'}
                status={lineageTracking.percentage >= 70 ? 'success' : 'active'}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Source Repository</span>
                <span style={{ fontWeight: 'bold' }}>{sourceRepoCoverage.percentage}%</span>
              </div>
              <Progress
                percent={sourceRepoCoverage.percentage}
                strokeColor={sourceRepoCoverage.percentage >= 80 ? '#52c41a' : '#faad14'}
                status={sourceRepoCoverage.percentage >= 80 ? 'success' : 'active'}
              />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Model Cards</span>
                <span style={{ fontWeight: 'bold' }}>{modelCards.percentage}%</span>
              </div>
              <Progress
                percent={modelCards.percentage}
                strokeColor={modelCards.percentage >= 60 ? '#52c41a' : '#faad14'}
                status={modelCards.percentage >= 60 ? 'success' : 'active'}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Recent Models Table */}
      <Card title="Recent Model Events">
        <Table
          dataSource={recentModels}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} model events`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_03_Dashboard;
